// ========================================================
// FAST HIT Website — Error Boundary
// ========================================================

import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-state" style={{ minHeight: '60vh' }}>
          <div className="error-state__icon">
            <AlertTriangle size={28} />
          </div>
          <h2 className="error-state__title">Something went wrong</h2>
          <p className="error-state__description">
            {this.state.error?.message || 'An unexpected error occurred. Please try again.'}
          </p>
          <button className="btn btn--primary" onClick={this.handleRetry}>
            <RefreshCw size={16} />
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
